import React, { useState, useEffect } from 'react';
import { statusApi, EnumDefinition } from '../../services/api';

interface Props {
  definitionType: string; // e.g., "categories", "eras", "conditions"
  title: string;          // e.g., "Category Definitions"
  organizationId?: string; // Optional organization ID (defaults to current user's org)
  readOnlyNames?: boolean; // For categories synced with Square
  squareSyncInfo?: string; // Info message for Square-synced types
  onSave?: () => void;
}

export const EnumDefinitionsManager: React.FC<Props> = ({
  definitionType,
  title,
  organizationId,
  readOnlyNames = false,
  squareSyncInfo,
  onSave
}) => {
  const [definitions, setDefinitions] = useState<EnumDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    loadDefinitions();
  }, [definitionType, organizationId]);

  const loadDefinitions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await statusApi.enumDefinitions.list(definitionType, organizationId);
      if (response.success && response.data) {
        console.log(`📥 Loaded ${definitionType} definitions:`, response.data.map(d => ({
          id: d.id,
          name: d.name,
          isTemp: d.id.startsWith('temp-')
        })));
        setDefinitions(response.data);
      } else {
        setError(response.message || 'Failed to load definitions');
      }
    } catch (err) {
      console.error('Failed to load definitions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load definitions');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    const newDef: EnumDefinition = {
      id: `temp-${Date.now()}`,
      organization_id: '',
      type: definitionType,
      name: '',
      description: '',
      sort_order: definitions.length,
    };
    setDefinitions([...definitions, newDef]);
  };

  const handleUpdate = (id: string, field: 'name' | 'description', value: string) => {
    setDefinitions(definitions.map(def =>
      def.id === id ? { ...def, [field]: value } : def
    ));
  };

  const handleSave = async (definition: EnumDefinition) => {
    try {
      setSaving(true);
      setError(null);

      console.log('💾 Saving definition:', {
        id: definition.id,
        type: definition.type,
        name: definition.name,
        isNew: definition.id.startsWith('temp-')
      });

      if (definition.id.startsWith('temp-')) {
        // Create new
        const response = await statusApi.enumDefinitions.create({
          type: definition.type,
          name: definition.name,
          description: definition.description,
          sort_order: definition.sort_order,
        }, organizationId);

        console.log('✅ Create response:', response);

        if (response.success && response.data) {
          setDefinitions(definitions.map(def =>
            def.id === definition.id ? response.data! : def
          ));
        } else {
          console.error('❌ Create failed:', response);
          setError(response.message || 'Failed to create definition');
        }
      } else {
        // Update existing
        const response = await statusApi.enumDefinitions.update(definition.id, {
          name: definition.name,
          description: definition.description,
          sort_order: definition.sort_order,
        });

        console.log('✅ Update response:', response);

        if (response.success) {
          // Reload definitions to get the updated data
          await loadDefinitions();
        } else {
          console.error('❌ Update failed:', response);
          setError(response.message || 'Failed to update definition');
        }
      }

      if (onSave) onSave();
    } catch (err) {
      console.error('❌ Exception during save:', err);
      setError(err instanceof Error ? err.message : 'Failed to save definition');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this definition?')) return;

    try {
      setError(null);
      if (!id.startsWith('temp-')) {
        const response = await statusApi.enumDefinitions.delete(id);
        if (!response.success) {
          setError(response.message || 'Failed to delete definition');
          return;
        }
      }
      setDefinitions(definitions.filter(def => def.id !== id));
      if (onSave) onSave();
    } catch (err) {
      console.error('Failed to delete definition:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete definition');
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newDefs = [...definitions];
    [newDefs[index - 1], newDefs[index]] = [newDefs[index], newDefs[index - 1]];
    newDefs.forEach((def, i) => def.sort_order = i);
    setDefinitions(newDefs);
  };

  const handleMoveDown = (index: number) => {
    if (index === definitions.length - 1) return;
    const newDefs = [...definitions];
    [newDefs[index], newDefs[index + 1]] = [newDefs[index + 1], newDefs[index]];
    newDefs.forEach((def, i) => def.sort_order = i);
    setDefinitions(newDefs);
  };

  if (loading) {
    return (
      <div className="text-gray-500 text-center py-4">
        Loading {title}...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-400 hover:text-gray-600"
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            {squareSyncInfo && (
              <p className="text-sm text-blue-600 mt-1">
                ℹ️ {squareSyncInfo}
              </p>
            )}
          </div>
        </div>
        {!readOnlyNames && isExpanded && (
          <button
            type="button"
            onClick={handleAdd}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            + Add Definition
          </button>
        )}
      </div>

      {error && isExpanded && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          ❌ {error}
        </div>
      )}

      {isExpanded && (
        <div className="space-y-2">
          {definitions.map((def, index) => (
          <div key={def.id} className="flex items-start gap-2 p-3 bg-white border border-gray-200 rounded-lg">
            {/* Reorder buttons */}
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => handleMoveUp(index)}
                disabled={index === 0}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                title="Move up"
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => handleMoveDown(index)}
                disabled={index === definitions.length - 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                title="Move down"
              >
                ▼
              </button>
            </div>

            {/* Name input */}
            <input
              type="text"
              value={def.name}
              onChange={(e) => handleUpdate(def.id, 'name', e.target.value)}
              disabled={readOnlyNames && !def.id.startsWith('temp-')}
              placeholder="Value name (e.g., Kimono)"
              className="w-1/4 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              title={readOnlyNames ? "Names are synced from Square (read-only)" : ""}
            />

            {/* Description input */}
            <input
              type="text"
              value={def.description}
              onChange={(e) => handleUpdate(def.id, 'description', e.target.value)}
              placeholder="What this value means..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />

            {/* Actions */}
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => handleSave(def)}
                disabled={!def.name || !def.description || saving}
                className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Save"
              >
                💾 Save
              </button>
              {!readOnlyNames && (
                <button
                  type="button"
                  onClick={() => handleDelete(def.id)}
                  className="px-3 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                  title="Delete"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>
        ))}

          {definitions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No definitions yet. {!readOnlyNames && "Click 'Add Definition' to get started."}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
